import i18nFactory from '@yandex-int/i18n';
import { Action } from 'redux';
import { Epic, ofType } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, mergeMap } from 'rxjs/operators';

import { NoticeType, showNoticeMessage } from '../../../actions/grid-screen';
import {
    addMarketerProduct,
    CHANGE_MARKETER_PRODUCT,
    changeMarketerAddProductForm,
    ChangeMarketerProductAction,
    FETCH_ADD_MARKETER_PRODUCT,
    FETCH_MARKETER_CARD_DATA,
    FETCH_REMOVE_MARKETER_PRODUCT,
    FetchMarketerCardAction,
    FetchRemoveMarketerProductAccessAction,
    marketersSetGridOffset,
    removeMarketerProductAccess,
    setMarketerCardPageStatus,
    updateMarketerCardData,
    updateMarketerProduct
} from '../../../actions/marketers/marketerCard';
import { selectMarketersSortParams } from '../../../selectors/marketers';
import { confirm } from '../../../service/Confirm/Confirm';
import { EpicDeps } from '../../../service/StoreFactory';
import {
    MarketerAccess,
    MarketersPageStates
} from '../../../typings/marketers';
import { State } from '../../../typings/state';

import * as keyset from './marketerCard.i18n';

const i18n = i18nFactory(keyset);

export const fetchMarketerCardDataEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) =>
    action$.pipe(
        ofType(FETCH_MARKETER_CARD_DATA),
        mergeMap(
            (action: FetchMarketerCardAction): Observable<Action> => {
                return from(
                    deps.marketersDataProvider.fetchMarketerCardData(action.id)
                ).pipe(
                    mergeMap(response => {
                        return of(
                            updateMarketerCardData(response),
                            setMarketerCardPageStatus(
                                MarketersPageStates.LOADED
                            )
                        );
                    }),
                    catchError(() =>
                        of(setMarketerCardPageStatus(MarketersPageStates.ERROR))
                    )
                );
            }
        )
    );

export const changeMarketerProductEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) =>
    action$.pipe(
        ofType(CHANGE_MARKETER_PRODUCT),
        mergeMap(
            (action: ChangeMarketerProductAction): Observable<Action> => {
                const {
                    marketerCard: {
                        marketer: { id }
                    }
                } = store$.value;

                return from(
                    deps.marketersDataProvider.fetchChangeMarketerProduct(
                        action.product,
                        id
                    )
                ).pipe(
                    mergeMap(response => {
                        showNoticeMessage(
                            NoticeType.success,
                            i18n('change-success')
                        );

                        return of(
                            updateMarketerProduct({
                                ...response,
                                productName: action.product.productName
                            }),
                            setMarketerCardPageStatus(
                                MarketersPageStates.LOADED
                            )
                        );
                    }),
                    catchError(() => {
                        showNoticeMessage(NoticeType.error, i18n('error'));

                        return of(
                            setMarketerCardPageStatus(
                                MarketersPageStates.PENDING
                            )
                        );
                    })
                );
            }
        )
    );

export const addMarketerProductEpic: Epic<Action, Action, State, EpicDeps> = (
    action$,
    store$,
    deps
) =>
    action$.pipe(
        ofType(FETCH_ADD_MARKETER_PRODUCT),
        mergeMap(
            (): Observable<Action> => {
                const {
                    productForm,
                    marketer: { id }
                } = store$.value.marketerCard;

                return from(
                    deps.marketersDataProvider.fetchMarketerAddProduct(
                        productForm,
                        id
                    )
                ).pipe(
                    mergeMap(response => {
                        const sortParams = selectMarketersSortParams(
                            store$.value
                        );

                        showNoticeMessage(
                            NoticeType.success,
                            i18n('add-success')
                        );

                        return of(
                            addMarketerProduct(
                                {
                                    ...response,
                                    productName: productForm.product.productName
                                },
                                sortParams
                            ),
                            setMarketerCardPageStatus(
                                MarketersPageStates.LOADED
                            ),
                            changeMarketerAddProductForm({
                                product: {
                                    productId: '',
                                    productName: ''
                                },
                                access: MarketerAccess.canRead,
                                isEnabled: false
                            })
                        );
                    }),
                    catchError(() => {
                        showNoticeMessage(NoticeType.error, i18n('error'));

                        return of(
                            setMarketerCardPageStatus(
                                MarketersPageStates.PENDING
                            )
                        );
                    })
                );
            }
        )
    );

export const removeMarketerProductEpic: Epic<
    Action,
    Action,
    State,
    EpicDeps
> = (action$, store$, deps) =>
    action$.pipe(
        ofType(FETCH_REMOVE_MARKETER_PRODUCT),
        mergeMap(
            (
                action: FetchRemoveMarketerProductAccessAction
            ): Observable<Action> => {
                const { marketer } = store$.value.marketerCard;
                const product = marketer.ownProducts.find(
                    item => item.id === action.productId
                );

                if (!product) {
                    return of(
                        setMarketerCardPageStatus(MarketersPageStates.ERROR)
                    );
                }

                return from(
                    confirm({
                        message: i18n('remove-confirm', {
                            product: product.productName
                        })
                    })
                ).pipe(
                    mergeMap(() =>
                        from(
                            deps.marketersDataProvider.fetchRemoveMarketerProduct(
                                action.productId,
                                marketer.id
                            )
                        ).pipe(
                            mergeMap(
                                (res): Observable<Action> => {
                                    if (res) {
                                        showNoticeMessage(
                                            NoticeType.success,
                                            i18n('remove-success')
                                        );

                                        return of(
                                            removeMarketerProductAccess(
                                                action.productId
                                            ),
                                            setMarketerCardPageStatus(
                                                MarketersPageStates.LOADED
                                            ),
                                            marketersSetGridOffset(1),
                                            marketersSetGridOffset(0)
                                        );
                                    }

                                    showNoticeMessage(
                                        NoticeType.error,
                                        i18n('error')
                                    );

                                    return of(
                                        updateMarketerProduct({
                                            id: product.id,
                                            productName: product.productName,
                                            access: product.access
                                        }),
                                        setMarketerCardPageStatus(
                                            MarketersPageStates.PENDING
                                        )
                                    );
                                }
                            ),
                            catchError(() => {
                                showNoticeMessage(
                                    NoticeType.error,
                                    i18n('error')
                                );

                                return of(
                                    updateMarketerProduct({
                                        id: product.id,
                                        productName: product.productName,
                                        access: product.access
                                    }),
                                    setMarketerCardPageStatus(
                                        MarketersPageStates.PENDING
                                    )
                                );
                            })
                        )
                    ),
                    catchError(() => of(updateMarketerProduct(product)))
                );
            }
        )
    );
